import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, EyeIcon, GaugeIcon, MessageSquareIcon, ShieldCheckIcon } from 'lucide-react';
import { PageCTA } from '../components/PageCTA';
const capabilities = [{
  icon: EyeIcon,
  title: 'Claridad de propuesta',
  description: 'Ayudamos a que el visitante pueda entender rápidamente qué haces, para quién y por qué elegirte.'
}, {
  icon: MessageSquareIcon,
  title: 'Caminos de contacto',
  description: 'Organizamos llamadas a la acción y puntos de contacto para facilitar una siguiente conversación.'
}, {
  icon: GaugeIcon,
  title: 'Experiencia ágil',
  description: 'Diseñamos con atención a lectura, jerarquía y comportamiento responsive para reducir fricción.'
}, {
  icon: ShieldCheckIcon,
  title: 'Confianza visible',
  description: 'Una presencia coherente hace más fácil que la primera impresión esté a la altura de tu negocio.'
}];
const scenarios = [{
  sector: 'Hostelería',
  challenge: 'Una carta difícil de consultar desde móvil y una web que no refleja la propuesta del local.',
  response: 'Una presencia editorial y una carta digital ordenada alrededor de categorías, horarios y acciones principales.',
  signal: 'Qué observar: facilidad para encontrar, elegir y contactar.'
}, {
  sector: 'Servicios locales',
  challenge: 'Información repartida, servicios poco definidos y una primera impresión que no explica la diferencia.',
  response: 'Una estructura que presenta servicios, proceso y puntos de contacto con una jerarquía más clara.',
  signal: 'Qué observar: calidad de las consultas y claridad de la propuesta.'
}, {
  sector: 'Marca profesional',
  challenge: 'Una presencia visual genérica que no acompaña el nivel de experiencia que se quiere comunicar.',
  response: 'Una dirección visual sobria y contenidos organizados para reforzar criterio, foco y confianza.',
  signal: 'Qué observar: percepción de marca y comprensión del servicio.'
}];
export function ResultsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen bg-[#0a0a0a] pt-24" data-id="element-495" data-mp="1gfr0bq6otl8d">
      <section className="border-b border-white/10 py-16 sm:py-24" data-id="element-496" data-mp="1ge7elk29ihz7">
        <div className="container mx-auto max-w-6xl px-6 md:px-12" data-id="element-497" data-mp="1l8bmq6n9kw1v">
          <motion.div initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="grid gap-7 lg:grid-cols-[0.8fr_1.2fr] lg:items-end" data-id="element-498" data-mp="1q6liojgtc0oj">
            <div data-id="element-499" data-mp="243ijndqnayj5">
              <p className="eyebrow" data-id="element-500" data-mp="4sh65tk6k0gc">Enfoque</p>
              <h1 className="mt-5 text-4xl font-medium tracking-[-0.055em] text-white sm:text-6xl" data-id="element-501" data-mp="4tsjajzimkk4">
                Diseñamos condiciones para que una presencia funcione mejor.
              </h1>
            </div>
            <p className="max-w-2xl text-base leading-relaxed text-neutral-400 sm:text-lg" data-id="element-502" data-mp="29m3nf1jh5nq">
              No usamos cifras genéricas como prueba. Ponemos el foco en los
              elementos que se pueden observar y mejorar: claridad, confianza,
              experiencia y facilidad para actuar.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-shell border-b border-white/10" data-id="element-503" data-mp="l76wex7fcodm">
        <div className="container mx-auto max-w-6xl px-6 md:px-12" data-id="element-504" data-mp="g7u8sa8qbd9z">
          <div className="grid overflow-hidden border border-white/10 sm:grid-cols-2 lg:grid-cols-4" data-id="element-505" data-mp="g8lsamp1xkjw">
            {capabilities.map((item, index) => {
            const Icon = item.icon;
            return <motion.article key={item.title} initial={{
              opacity: 0,
              y: 14
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              amount: 0.25
            }} transition={{
              duration: 0.4,
              delay: index * 0.05
            }} className="min-h-[245px] border-b border-white/10 bg-black p-7 last:border-b-0 sm:border-r sm:even:border-r-0 lg:border-b-0 lg:even:border-r lg:last:border-r-0" data-id="element-506" data-mp="6c0no2gy2bgl" data-mpi={index}>
                  <Icon size={20} className="text-white" aria-hidden="true" data-id="element-507" data-mp="17esu1tc2blga" />
                  <h2 className="mt-12 text-lg font-medium text-white" data-id="element-508" data-mp="sh0gh6q2t74q">
                    {item.title}
                  </h2>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-400" data-id="element-509" data-mp="xfac1ifpq9qv">
                    {item.description}
                  </p>
                </motion.article>;
          })}
          </div>
        </div>
      </section>

      <section className="section-shell border-b border-white/10 bg-black" aria-labelledby="scenarios-title" data-id="element-510" data-mp="3i7lx9rrkm507">
        <div className="container mx-auto max-w-6xl px-6 md:px-12" data-id="element-511" data-mp="1v9ussbezxchz">
          <div className="grid gap-7 border-b border-white/10 pb-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-end" data-id="element-512" data-mp="1vbp4cz4dns9e">
            <div data-id="element-513" data-mp="297181w0igt2g">
              <p className="eyebrow" data-id="element-514" data-mp="2581x9us3m233">Escenarios de aplicación</p>
              <h2 id="scenarios-title" className="mt-5 text-3xl font-medium tracking-[-0.045em] text-white sm:text-4xl" data-id="element-515" data-mp="255o2ln1byrw2">
                Cómo se traduce el enfoque según el contexto.
              </h2>
            </div>
            <p className="max-w-2xl text-sm leading-relaxed text-neutral-400 sm:text-base" data-id="element-516" data-mp="1qbjgzv554rx4">
              Estos escenarios son ilustrativos. No representan casos de éxito
              ni resultados atribuidos a clientes específicos.
            </p>
          </div>
          <div className="mt-10 space-y-4" data-id="element-517" data-mp="tsvcego6ubi4">
            {scenarios.map((scenario, index) => <motion.article key={scenario.sector} initial={{
            opacity: 0,
            y: 14
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.4,
            delay: index * 0.05
          }} className="border border-white/10 bg-[#0a0a0a] p-7 sm:p-8" data-id="element-518" data-mp="2sxn20rdx5qdu" data-mpi={index}>
                <div className="flex items-start justify-between gap-5" data-id="element-519" data-mp="2jwowu5rqvcto">
                  <h3 className="text-xl font-medium tracking-[-0.025em] text-white" data-id="element-520" data-mp="2jyrwlhip8kbl">
                    {scenario.sector}
                  </h3>
                  <ArrowUpRightIcon size={18} className="text-neutral-600" aria-hidden="true" data-id="element-521" data-mp="3vcdmx60aoryp" />
                </div>
                <div className="mt-7 grid gap-6 md:grid-cols-3" data-id="element-522" data-mp="1oo3dvbk0141s">
                  <div data-id="element-523" data-mp="1opejzi0x2nw2">
                    <p className="eyebrow" data-id="element-524" data-mp="1joqr0qt4nwz7">Situación</p>
                    <p className="mt-3 text-sm leading-relaxed text-neutral-400" data-id="element-525" data-mp="1jpabae692xge">
                      {scenario.challenge}
                    </p>
                  </div>
                  <div data-id="element-526" data-mp="19xnqbq441k0g">
                    <p className="eyebrow" data-id="element-527" data-mp="19yffai37cjhr">Dirección</p>
                    <p className="mt-3 text-sm leading-relaxed text-neutral-300" data-id="element-528" data-mp="14zup7bhnzg71">
                      {scenario.response}
                    </p>
                  </div>
                  <div data-id="element-529" data-mp="vywiumjr6wla">
                    <p className="eyebrow" data-id="element-530" data-mp="vyefz72l5k4e">Indicador</p>
                    <p className="mt-3 text-sm leading-relaxed text-white" data-id="element-531" data-mp="w10md3dqptl1">
                      {scenario.signal}
                    </p>
                  </div>
                </div>
              </motion.article>)}
          </div>
        </div>
      </section>
      <PageCTA data-id="element-532" data-mp="2lf5ma3fg9fcf" />
    </div>;
}